import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Phone, MessageCircle, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { BottomSheet, BottomSheetContent, BottomSheetTitle, BottomSheetDescription } from '@/components/ui/bottom-sheet'
import { phoneVerificationService, type InitiateOtpResponse, type VerifyOtpResponse } from '@/services/phoneVerificationService'
import { toast } from 'sonner'

interface PhoneVerificationBannerProps {
  open: boolean
  onDismiss: () => void
  onVerified: (phoneNumber: string) => void
}

type Step = 'phone' | 'otp' | 'success'

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function PhoneVerificationBanner({ open, onDismiss, onVerified }: PhoneVerificationBannerProps) {
  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [otpToken, setOtpToken] = useState('')
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [attempts, setAttempts] = useState(0)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open])

  useEffect(() => {
    if (!expiresAt || step !== 'otp') return
    const end = new Date(expiresAt).getTime()
    const update = () => {
      const now = Date.now()
      const left = Math.max(0, Math.ceil((end - now) / 1000))
      setRemainingSeconds(left)
      if (left === 0) {
        cleanupTimer()
      }
    }
    update()
    timerRef.current = window.setInterval(update, 1000)
    return () => cleanupTimer()
  }, [expiresAt, step])

  const cleanupTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const resetForm = () => {
    setStep('phone')
    setPhone('')
    setOtp('')
    setOtpToken('')
    setExpiresAt(null)
    setRemainingSeconds(0)
    setAttempts(0)
    cleanupTimer()
  }

  const normalizePhone = (value: string) => value.replace(/\D/g, '').slice(0, 12)

  const handleInitiate = async () => {
    const clean = normalizePhone(phone)
    if (clean.length < 8) {
      toast.error('Veuillez entrer un numéro valide')
      return
    }
    setIsSubmitting(true)
    try {
      const result: InitiateOtpResponse = await phoneVerificationService.initiateOtp(clean)
      if (result.success && result.token && result.expiresAt) {
        setOtpToken(result.token)
        setExpiresAt(result.expiresAt)
        setStep('otp')
        toast.success('Code envoyé sur WhatsApp')
      } else {
        toast.error(result.error || "Erreur lors de l'envoi")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleVerify = async () => {
    if (otp.length !== 6) {
      toast.error('Le code doit contenir 6 chiffres')
      return
    }
    if (remainingSeconds === 0) {
      toast.error('Code expiré, veuillez renvoyer un code')
      return
    }
    setIsSubmitting(true)
    try {
      const result: VerifyOtpResponse = await phoneVerificationService.verifyOtp(otpToken, otp)
      if (result.success && result.phoneNumber) {
        setStep('success')
        toast.success(`Numéro ${result.phoneNumber} validé`)
        onVerified(result.phoneNumber)
      } else {
        setAttempts((a) => a + 1)
        toast.error(result.error || 'Code incorrect')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResend = async () => {
    setOtp('')
    setAttempts(0)
    await handleInitiate()
  }

  const phoneClean = normalizePhone(phone)
  const phoneValid = phoneClean.length >= 8
  const otpComplete = otp.length === 6

  return (
    <>
      <AnimatePresence>
        {open && step !== 'success' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="shrink-0 border-b border-border bg-gradient-to-r from-blue-500/10 to-green-500/10"
          >
            <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-2.5 lg:max-w-6xl lg:px-8">
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-green-500/20 text-green-600">
                <Phone className="size-4" />
              </span>
              <div className="min-w-0 grow">
                <p className="text-xs font-semibold text-foreground">Vérifiez votre numéro WhatsApp</p>
                <p className="text-[11px] text-muted-foreground">Recevez les notifications et sécurisez votre compte.</p>
              </div>
              <button
                onClick={() => { setStep('phone'); onDismiss(); }}
                className="shrink-0 rounded-full bg-foreground px-3 py-1.5 text-[11px] font-semibold text-background"
              >
                Vérifier
              </button>
              <button onClick={onDismiss} aria-label="Fermer" className="text-muted-foreground hover:text-foreground">
                <X className="size-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomSheet open={open && step !== 'success'} onOpenChange={(v) => { if (!v) { resetForm(); onDismiss(); } }}>
        <BottomSheetContent className="bg-card text-foreground">
          <BottomSheetTitle className="sr-only">Vérification du numéro WhatsApp</BottomSheetTitle>
          <BottomSheetDescription className="sr-only">Saisissez votre numéro et le code reçu sur WhatsApp.</BottomSheetDescription>
          <div className="mx-auto w-full max-w-[44ch]">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-green-600">Sécurité</p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight">
                  {step === 'otp' ? 'Code de vérification' : 'Valider votre numéro'}
                </h2>
                <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                  {step === 'otp'
                    ? `Saisissez le code à 6 chiffres envoyé sur WhatsApp au +${phoneClean}.`
                    : 'Recevez les notifications importantes et sécurisez votre compte.'}
                </p>
              </div>
            </div>

            {step === 'phone' && (
              <div className="space-y-4">
                <label className="block">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Numéro WhatsApp</span>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 rounded-lg bg-green-500/10 p-2">
                      <Phone className="size-4 text-green-600" />
                    </div>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(normalizePhone(e.target.value))}
                      inputMode="tel"
                      placeholder="Ex : 22660356506"
                      className="w-full rounded-xl bg-surface py-3 pl-12 pr-4 text-sm tabular-nums ring-1 ring-border outline-none transition focus:ring-foreground/20"
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">Format accepté : 226XXXXXXXX ou 07XXXXXXXX</p>
                </label>

                <button
                  disabled={!phoneValid || isSubmitting}
                  onClick={handleInitiate}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-foreground py-3.5 text-sm font-semibold text-background transition disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : <MessageCircle className="size-4" />}
                  {isSubmitting ? 'Envoi...' : 'Envoyer le code WhatsApp'}
                </button>
              </div>
            )}

            {step === 'otp' && (
              <div className="space-y-4">
                <label className="block">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Code à 6 chiffres</span>
                  <input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric"
                    placeholder="020608"
                    className="w-full rounded-xl bg-surface px-4 py-3 text-center text-lg font-semibold tracking-[0.2em] tabular-nums ring-1 ring-border outline-none transition focus:ring-foreground/20"
                  />
                </label>

                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground">
                    Expire dans : <span className={remainingSeconds <= 60 ? 'text-destructive font-semibold' : 'font-semibold text-foreground'}>{formatTime(remainingSeconds)}</span>
                  </span>
                  {attempts > 0 && (
                    <span className="text-destructive">Tentative {attempts}/3</span>
                  )}
                </div>

                <button
                  disabled={!otpComplete || isSubmitting || remainingSeconds === 0}
                  onClick={handleVerify}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-foreground py-3.5 text-sm font-semibold text-background transition disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}
                  {isSubmitting ? 'Vérification...' : 'Confirmer'}
                </button>

                <button
                  onClick={handleResend}
                  disabled={isSubmitting || remainingSeconds > 240}
                  className="w-full text-center text-[11px] font-medium text-muted-foreground transition hover:text-foreground disabled:opacity-40"
                >
                  Renvoyer le code
                </button>
              </div>
            )}
          </div>
        </BottomSheetContent>
      </BottomSheet>

      <AnimatePresence>
        {open && step === 'success' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { resetForm(); onDismiss(); }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/55 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-2xl bg-card p-6 text-center shadow-2xl ring-1 ring-border"
            >
              <div className="mx-auto mb-4 grid size-14 place-items-center rounded-full bg-green-500/20">
                <CheckCircle2 className="size-7 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Numéro validé !</h3>
              <p className="mt-2 text-sm text-muted-foreground">Votre numéro WhatsApp est maintenant vérifié.</p>
              <button
                onClick={() => { resetForm(); onDismiss(); }}
                className="mt-5 w-full rounded-xl bg-foreground py-3 text-sm font-semibold text-background transition active:scale-[0.98]"
              >
                Continuer
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
